import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	submitFeedbackV2HousesHouseIdAssistantFeedbackPostMutation,
	getFeedbackStateV2HousesHouseIdAssistantFeedbackStateGetOptions,
} from '@/client/@tanstack/react-query.gen';
import type { FeedbackRating } from '@/client';
import { useHouseContext } from '@/context/HouseContext';

export const FEEDBACK_RATING = {
	THUMBS_UP: 'thumbs_up',
	THUMBS_DOWN: 'thumbs_down',
} as const;

/**
 * Encapsulates feedback submission and existing-rating lookup for a given exchange.
 *
 * Hydration uses the lightweight `feedback/state` endpoint, which returns only the current user's
 * (exchange_id, rating) pairs — NOT the admin-only `list_feedback` endpoint (full records/comments).
 * Normal users get feedback *state*, not feedback *browsing*. The state endpoint has no exchange_id
 * filter, so we match client-side. Invalidates the state query on submit so the thumb reflects it.
 */
export const useFeedback = (exchangeId: string) => {
	const { selectedHouse } = useHouseContext();
	const queryClient = useQueryClient();
	const houseId = selectedHouse?.id;

	const feedbackQuery = useQuery({
		...getFeedbackStateV2HousesHouseIdAssistantFeedbackStateGetOptions({
			// The query stays disabled until HouseProvider's selection effect has resolved a house.
			path: { house_id: houseId ?? 0 },
		}),
		enabled: houseId != null,
	});

	const existingFeedback = feedbackQuery.data?.find((f) => f.exchange_id === exchangeId) ?? null;

	const { mutate: submitFeedback, isPending } = useMutation({
		...submitFeedbackV2HousesHouseIdAssistantFeedbackPostMutation(),
		onSuccess: () => {
			if (houseId == null) return;
			queryClient.invalidateQueries(
				getFeedbackStateV2HousesHouseIdAssistantFeedbackStateGetOptions({
					path: { house_id: houseId },
				}),
			);
		},
	});

	const submit = (rating: FeedbackRating, comment?: string) => {
		if (houseId == null) return;
		submitFeedback({
			path: { house_id: houseId },
			body: { exchange_id: exchangeId, rating, comment: comment ?? null },
		});
	};

	return { existingFeedback, submit, isPending, canSubmit: houseId != null };
};
